import type {
    CreativeXAdFormats,
    CreativeXCampaignObjectiveRequest,
    CreativeXChannel,
    CreativeXDimensions,
    CreativeXPlacements,
    CreativeXPublishers
} from "@/types/preflight/creativeX";

export default function formatCreativeXDimensions(creativeXDimensions?: CreativeXDimensions) {
    if (!creativeXDimensions) {
        return null;
    }

    const brands = creativeXDimensions.brands.map(brand => ({
        value: brand.id.toString(),
        label: `${brand.id} (${brand.name})`
    }));

    const markets = creativeXDimensions.markets
        .sort((a, b) => a.name.localeCompare(b.name))
        .filter(market => market.iso_3166_code)
        .map(market => ({
            value: market.iso_3166_code,
            label: `${market.iso_3166_code} - ${market.name}`
        }));

    const channels = creativeXDimensions.channels.map(channel => ({
        value: channel.name,
        label: channel.name
    }));

    const adFormats = creativeXDimensions.channels.reduce(
        (acc, channel) => {
            acc[channel.name] = channel.ad_formats.map(format => ({
                value: format.name,
                label: format.name
            }));
            return acc;
        },
        {} as Record<CreativeXChannel, { value: CreativeXAdFormats; label: CreativeXAdFormats }[]>
    );

    const campaignObjectives = creativeXDimensions.channels.reduce(
        (acc, channel) => {
            acc[channel.name] = channel.campaign_objectives.map(objective => ({
                value: objective,
                label: objective
            }));
            return acc;
        },
        {} as Record<
            CreativeXChannel,
            { value: CreativeXCampaignObjectiveRequest; label: CreativeXCampaignObjectiveRequest }[]
        >
    );

    const publishers = creativeXDimensions.channels.reduce(
        (acc, channel) => {
            acc[channel.name] = channel.publishers.map(publisher => ({
                value: publisher.name,
                label: publisher.name,
                placements: publisher.placements?.map(placement => ({
                    value: placement,
                    label: placement
                }))
            }));

            return acc;
        },
        {} as Record<
            CreativeXChannel,
            {
                value: CreativeXPublishers;
                label: CreativeXPublishers;
                placements: { value: CreativeXPlacements; label: CreativeXPlacements }[] | undefined;
            }[]
        >
    );

    return {
        brands,
        markets,
        channels,
        adFormats,
        campaignObjectives,
        publishers
    };
}
